/**
 * Chat timeline grouping utility.
 *
 * Takes a flat DocChatItem[] and returns a grouped structure with
 * date dividers inserted between different calendar days and
 * consecutive same-user messages within 5 minutes grouped together.
 */

import { isToday, isYesterday, format, parseISO, differenceInMinutes } from "date-fns";
import type { DocChatItem } from "@/lib/erp/docs";

const GROUPING_WINDOW_MINUTES = 5;

/** Frappe returns UTC timestamps without Z suffix — normalize for correct parsing. */
function normalizeTimestamp(ts: string): string {
  return ts.endsWith("Z") ? ts : ts + "Z";
}

// ── Public types ──────────────────────────────────────────────

export type ChatGroup = {
  type: "group";
  owner: string;
  user_full_name: string;
  user_avatar?: string | null;
  items: DocChatItem[];
};

export type ChatDateDivider = {
  type: "date-divider";
  label: string;
  date: string; // YYYY-MM-DD for keying
};

export type ChatActivityEntry = {
  type: "activity";
  item: DocChatItem;
};

export type ChatTimelineEntry = ChatGroup | ChatDateDivider | ChatActivityEntry;

// ── Helpers ───────────────────────────────────────────────────

function dateKey(creation: string): string {
  return creation.slice(0, 10); // YYYY-MM-DD from ISO string
}

function dateDividerLabel(dateStr: string): string {
  const date = parseISO(normalizeTimestamp(dateStr));
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}

function canGroup(prev: DocChatItem, curr: DocChatItem): boolean {
  if (prev.owner !== curr.owner) return false;
  const diff = Math.abs(
    differenceInMinutes(
      parseISO(normalizeTimestamp(curr.creation)),
      parseISO(normalizeTimestamp(prev.creation))
    )
  );
  return diff < GROUPING_WINDOW_MINUTES;
}

// ── Main function ─────────────────────────────────────────────

/**
 * Groups a chronologically-sorted DocChatItem[] into timeline entries
 * with date dividers and message groups.
 */
export function groupTimelineItems(items: DocChatItem[]): ChatTimelineEntry[] {
  if (items.length === 0) return [];

  const entries: ChatTimelineEntry[] = [];
  let lastDateKey = "";
  let currentGroup: ChatGroup | null = null;

  const flushGroup = () => {
    if (currentGroup) {
      entries.push(currentGroup);
      currentGroup = null;
    }
  };

  for (const item of items) {
    const dk = dateKey(item.creation);

    // Insert date divider if day changed
    if (dk !== lastDateKey) {
      flushGroup();
      entries.push({
        type: "date-divider",
        label: dateDividerLabel(item.creation),
        date: dk,
      });
      lastDateKey = dk;
    }

    // Activity events are standalone — never grouped
    if (item.type === "activity") {
      flushGroup();
      entries.push({ type: "activity", item });
      continue;
    }

    // Comment: try to append to current group
    if (
      currentGroup &&
      canGroup(currentGroup.items[currentGroup.items.length - 1], item)
    ) {
      currentGroup.items.push(item);
    } else {
      flushGroup();
      currentGroup = {
        type: "group",
        owner: item.owner,
        user_full_name: item.user_full_name,
        user_avatar: item.user_avatar,
        items: [item],
      };
    }
  }

  flushGroup();
  return entries;
}
